/**
 * useOwnByTile — the viewer's own chains on the tiles the wall has loaded, beyond `/mine`'s window
 * (`thinking/plans/2026_09_24_mine_beyond_the_cap.md` § 2). `/mine` names the newest
 * `MINE_ROOT_CAP` chains and places the wall from them; a chain older than that still owns its
 * tile's face, and this is where the face comes from: one `GET /mine?tiles=` per loaded page
 * (`WALL_PAGE_SIZE` ids), each tile id asked once per signed-in page session.
 *
 * Nothing here is polled: a take landing on an old chain is that chain's newest row, which puts it
 * in `/mine`'s own window, and `buildChains` keeps the newest row of a chain named twice. The rows
 * live in `useMine`'s state (`MineState.byTile`), where every patch reaches them; this hook only
 * asks. The asked set is dropped on sign-out, the only moment the answer changes owner.
 */
import {useEffect, useRef} from 'react'
import {WALL_PAGE_SIZE, isTake, type MineTilesResponse, type Take} from '@core/wall/contract'

/** The ids of `tileIds` not yet in `asked`, in order, cut into requests of at most `size` — a page
 *  that arrives alone is exactly one request. */
export function planTileAsks(tileIds: readonly number[], asked: ReadonlySet<number>, size = WALL_PAGE_SIZE): number[][] {
    const fresh: number[] = []
    const seen = new Set<number>()
    for (const id of tileIds) {
        if (asked.has(id) || seen.has(id)) continue
        seen.add(id)
        fresh.push(id)
    }
    const asks: number[][] = []
    for (let i = 0; i < fresh.length; i += size) asks.push(fresh.slice(i, i + size))
    return asks
}

export function useOwnByTile(
    /** Signed in, with a `/mine` to hold the rows (`useMine`'s `mergeByTile` drops them otherwise). */
    ready: boolean,
    tileIds: readonly number[],
    onRows: (rows: readonly Take[]) => void,
): void {
    const askedRef = useRef<Set<number>>(new Set())

    useEffect(() => {
        if (!ready) askedRef.current = new Set()
    }, [ready])

    useEffect(() => {
        if (!ready) return
        const asked = askedRef.current
        for (const ids of planTileAsks(tileIds, asked)) {
            // Marked before the fetch, so the render the response triggers does not ask again;
            // un-marked on failure, so the next page load retries them with its own ask.
            for (const id of ids) asked.add(id)
            const forget = () => {
                for (const id of ids) asked.delete(id)
            }
            void (async () => {
                try {
                    const res = await fetch(`/mine?tiles=${ids.join(',')}`, {credentials: 'same-origin'})
                    if (!res.ok) {
                        console.error(`[useOwnByTile] GET /mine?tiles= failed with status ${res.status}`)
                        forget()
                        return
                    }
                    const data = (await res.json()) as MineTilesResponse
                    // The sign-out reset replaced the set while this was in flight: another owner's rows.
                    if (askedRef.current !== asked) return
                    if (!Array.isArray(data?.done) || !data.done.every(isTake)) {
                        forget()
                        return
                    }
                    if (data.done.length > 0) onRows(data.done)
                } catch {
                    forget()
                }
            })()
        }
    }, [ready, tileIds, onRows])
}
